import {makeAutoObservable} from 'mobx';
import {toast} from 'react-toastify';
import FeedbackRepository from '../../../data/repositories/FeedbackRepository';
import {
  CreateFeedbackPayload,
  Feedback,
} from '../../../data/models/Feedback/Feedback';
import LoadingModule from '../../../modules/LoadingModule/LoadingModule';
import {getErrorFromCode} from '../../../data/errorCodes';
import {toastConfig} from '../../../uiToolkit/Toast/toastConfig';
import {feedbackText} from '../feedbackText';

export const FEEDBACK_PER_PAGE = 20;

class FeedbackStore {
  private feedbackRepo = new FeedbackRepository();
  private loadingModule = new LoadingModule();
  private editLoadingModule = new LoadingModule();

  constructor() {
    makeAutoObservable(this);
  }

  public get isLoading() {
    return this.loadingModule.isLoading;
  }

  public get isEditLoading() {
    return this.editLoadingModule.isLoading;
  }

  private handleErrors(errors?: string[]) {
    if (errors && errors[0]) {
      toast.error(getErrorFromCode(errors[0]), toastConfig);
    }
  }

  public async getFeedbackList(
    organizationId: string,
    page: number,
    search?: string,
  ) {
    this.loadingModule.startLoading();
    const result = await this.feedbackRepo.getFeedbackList(
      organizationId,
      page,
      FEEDBACK_PER_PAGE,
      search,
    );
    this.loadingModule.endLoading();
    if (result.success && result.data) {
      return result.data;
    }
    this.handleErrors(result.errors);
    return {
      pagesCount: 0,
      data: [] as Feedback[],
    };
  }

  public async getFeedback(organizationId: string, feedbackId: string) {
    this.loadingModule.startLoading();
    const result = await this.feedbackRepo.getFeedback(
      organizationId,
      feedbackId,
    );
    this.loadingModule.endLoading();
    if (result.success && result.data) {
      return result.data;
    }
    this.handleErrors(result.errors);
    return undefined;
  }

  public async createFeedback(
    organizationId: string,
    payload: CreateFeedbackPayload,
  ) {
    this.editLoadingModule.startLoading();
    const result = await this.feedbackRepo.createFeedback(
      organizationId,
      payload,
    );
    this.editLoadingModule.endLoading();
    if (!result.success) {
      this.handleErrors(result.errors);
      return undefined;
    }
    toast.success(feedbackText.feedbackCreated, toastConfig);
    return result.data;
  }

  public async deleteFeedback(organizationId: string, feedbackId: string) {
    this.editLoadingModule.startLoading();
    const result = await this.feedbackRepo.deleteFeedback(
      organizationId,
      feedbackId,
    );
    this.editLoadingModule.endLoading();
    if (!result.success) {
      this.handleErrors(result.errors);
      return false;
    }
    toast.success(feedbackText.feedbackDeleted, toastConfig);
    return true;
  }
}

export default FeedbackStore;
